import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { formatApiDate, toastHandler } from "../../utils/utils";

/**
 * A single purchasable Quest Type, as returned by the marketplace API.
 * Field names follow the backend response and are not remapped.
 * @typedef {Object} QuestType
 * @property {number} id
 * @property {string} name
 * @property {string} slug
 * @property {string} image - Media path, resolved against REACT_APP_MEDIA_URL_ORG.
 * @property {string} price - Decimal string, e.g. "100.00".
 * @property {boolean} owned
 * @property {string|null} acquired_via - "purchased", "default",
 *   "free_access" (Organisation Free Access) or null.
 * @property {string|null} expires_at - ISO date; always backend supplied.
 * @property {boolean} is_expired
 */

/**
 * A Bundle groups Quest Types into a marketplace tab. It carries no price —
 * pricing moved to the individual Quest Type.
 * @typedef {Object} Bundle
 * @property {number} id
 * @property {string} name
 * @property {boolean} isDefault - Default bundles are free and never expire.
 * @property {QuestType[]} quest_types
 */

/**
 * GET /admin/quest-types/marketplace
 * @typedef {Object} MarketplaceResponse
 * @property {Bundle[]} bundles
 * @property {boolean} [organisation_has_free_access] - Set by Super Admin;
 *   when true every quest type is granted through free access.
 */

/**
 * One entry in the Quest Type cart.
 * @typedef {Object} CartItem
 * @property {number} id - Quest Type ID, unique within the cart.
 * @property {string} name
 * @property {string} price
 * @property {string} [image]
 * @property {number} [bundleId]
 * @property {string} [bundleName]
 */

/**
 * Response of POST /admin/quest-types/checkout. Either a Stripe payment intent
 * or a free grant — never both.
 * @typedef {Object} CheckoutResponse
 * @property {string} [client_secret]
 * @property {string} [payment_intent_id]
 * @property {number} [quest_type_payment_id]
 * @property {number} [amount] - Total in cents, calculated by the backend.
 * @property {Array<{id: number, name: string, price: string}>} [items]
 * @property {string} [public_key]
 * @property {boolean} [is_free]
 * @property {string|null} [free_access_until] - Present only when the grant
 *   came from Organisation Free Access; absent for a legacy $0 cart.
 * @property {number[]} [purchased_incident_type_ids]
 * @property {string} [message]
 */

// Initial state for the quest type package slice
const initialState = {
  // Legacy bundle/category marketplace (kept for backward compatibility)
  questTypePackageData: {
    data: [],
  },
  status: "idle",
  error: null,
  // New per-Quest-Type marketplace
  marketplaceBundles: [],
  // Organisation Free Access, straight from the marketplace response
  organisationHasFreeAccess: false,
  marketplaceStatus: "idle",
  marketplaceError: null,
  // Quest Type cart (works across bundles, one entry per Quest Type id)
  cart: [],
  checkoutStatus: "idle",
  confirmStatus: "idle",
};

// Destructure the httpRequest function from Http service
const { httpRequest } = Http();

/**
 * Async thunk for fetching the list of quest type packages from marketplace.
 * @returns {Array} - List of quest type packages.
 */
export const questTypePackageList = createAsyncThunk(
  "listQuestTypePackage",
  async ({ pageCount, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/incidents/categories/marketplace?page=${pageCount}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch quest type packages", {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for creating payment intent.
 * @param {number} categoryId - The ID of the category to purchase.
 * @returns {Object} - The response containing payment_intent_id.
 */
export const createPaymentIntent = createAsyncThunk(
  "createPaymentIntent",
  async ({ categoryId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: `admin/incidents/categories/payment-intent`,
          body: { category_id: categoryId },
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to create payment intent", {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for confirming purchase.
 * @param {string} paymentIntentId - The payment intent ID from the first API.
 * @returns {Object} - The response from the confirm purchase request.
 */
export const confirmPurchase = createAsyncThunk(
  "confirmPurchase",
  async ({ paymentIntentId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: `admin/incidents/categories/confirm-purchase`,
          body: { payment_intent_id: paymentIntentId },
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to confirm purchase", {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for claiming a free quest type package.
 * @param {number} categoryId - The ID of the free category to claim.
 * @returns {Object} - The response from the claim free request.
 */
export const claimFreePackage = createAsyncThunk(
  "claimFreePackage",
  async ({ categoryId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: `admin/incidents/categories/claim-free`,
          body: { category_id: categoryId },
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to claim free package", {
        cause: err.cause,
      });
    }
  }
);

// ---------------------------------------------------------------------------
// New per-Quest-Type marketplace / cart / checkout
// ---------------------------------------------------------------------------

/**
 * Normalise a thunk error into the { message, status } shape used by the new
 * marketplace reducers, so callers can branch on the HTTP status code.
 */
const questTypeError = (err, fallback) => ({
  message: err.message || fallback,
  status: err.status,
});

/**
 * Async thunk for fetching the Quest Type marketplace.
 * Returns bundles (grouping tabs) each holding their own priced quest types.
 * @returns {{ bundles: Bundle[] }}
 */
export const questTypeMarketplace = createAsyncThunk(
  "questTypeMarketplace",
  async ({ navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/quest-types/marketplace`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(
        questTypeError(err, "Failed to fetch quest types"),
        { cause: err.cause }
      );
    }
  }
);

/**
 * Async thunk for checking out the Quest Type cart.
 * The backend calculates the amount — never send a frontend total.
 * @param {number[]} incidentTypeIds - Distinct Quest Type IDs to purchase.
 * @returns {CheckoutResponse}
 */
export const questTypeCheckout = createAsyncThunk(
  "questTypeCheckout",
  async ({ incidentTypeIds, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: `admin/quest-types/checkout`,
          body: { incident_type_ids: incidentTypeIds },
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(questTypeError(err, "Failed to start checkout"), {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for confirming a paid Quest Type purchase.
 * Only call this once Stripe has confirmed the PaymentIntent.
 * @param {string} paymentIntentId - The confirmed Stripe PaymentIntent ID.
 * @returns {{ message: string, purchased_incident_type_ids: number[] }}
 */
export const questTypeConfirmPurchase = createAsyncThunk(
  "questTypeConfirmPurchase",
  async ({ paymentIntentId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: `admin/quest-types/confirm-purchase`,
          body: { payment_intent_id: paymentIntentId },
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(
        questTypeError(err, "Failed to confirm purchase"),
        { cause: err.cause }
      );
    }
  }
);

// Create a slice for quest type package-related actions and state management
const questTypePackageSlice = createSlice({
  name: "questTypePackage",
  initialState,
  reducers: {
    /**
     * Reducer for showing toast messages.
     * @param {Object} state - The current state.
     * @param {Object} action - The action containing payload data.
     */
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, locationState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, locationState);
    },
    /**
     * Reducer for resetting the quest type package state.
     */
    resetQuestTypePackageState: (state) => {
      state.questTypePackageData = { data: [] };
      state.status = "idle";
      state.error = null;
    },
    /**
     * Add a Quest Type to the cart. Default-bundle and duplicate quest types
     * are rejected here so they can never reach the checkout payload.
     * @param {{ payload: CartItem }} action
     */
    addToCart: (state, action) => {
      const item = action.payload;
      if (!item?.id || item.isDefault) return;
      if (state.cart.some((cartItem) => cartItem.id === item.id)) return;
      state.cart.push(item);
    },
    /**
     * Remove a single Quest Type from the cart by its ID.
     */
    removeFromCart: (state, action) => {
      state.cart = state.cart.filter((item) => item.id !== action.payload);
    },
    /**
     * Empty the cart (after a successful purchase or an explicit clear).
     */
    clearCart: (state) => {
      state.cart = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // --- Quest Type marketplace ---------------------------------------
      .addCase(questTypeMarketplace.pending, (state, action) => {
        state.marketplaceStatus = "loading";
        state.marketplaceError = null;
        action.meta.arg?.setLoader?.(true);
      })
      .addCase(questTypeMarketplace.fulfilled, (state, action) => {
        state.marketplaceStatus = "succeeded";
        state.marketplaceBundles = action.payload?.bundles || [];
        state.organisationHasFreeAccess = Boolean(
          action.payload?.organisation_has_free_access
        );
        action.meta.arg?.setLoader?.(false);
      })
      .addCase(questTypeMarketplace.rejected, (state, action) => {
        state.marketplaceStatus = "failed";
        state.marketplaceError = action.payload?.message;
        action.meta.arg?.setLoader?.(false);
        questTypePackageSlice.caseReducers.showToast(state, {
          payload: { success: false, message: action.payload?.message },
        });
      })

      // --- Quest Type checkout ------------------------------------------
      .addCase(questTypeCheckout.pending, (state, action) => {
        state.checkoutStatus = "loading";
        action.meta.arg?.setLoader?.(true);
      })
      .addCase(questTypeCheckout.fulfilled, (state, action) => {
        state.checkoutStatus = "succeeded";
        action.meta.arg?.setLoader?.(false);
        // Free checkout already grants access — no Stripe, no confirm-purchase.
        if (action.payload?.is_free) {
          state.cart = [];
          state.marketplaceStatus = "idle";
          // Organisation Free Access is told apart from a legacy $0 cart by
          // free_access_until; only that one names the free-access date.
          const freeAccessUntil = action.payload?.free_access_until;
          questTypePackageSlice.caseReducers.showToast(state, {
            payload: {
              success: true,
              message: freeAccessUntil
                ? `Quest types granted successfully. Your organisation has free access until ${formatApiDate(
                    freeAccessUntil
                  )}.`
                : action.payload?.message ||
                  "Quest types granted successfully (free).",
            },
          });
        }
      })
      .addCase(questTypeCheckout.rejected, (state, action) => {
        state.checkoutStatus = "failed";
        action.meta.arg?.setLoader?.(false);
        questTypePackageSlice.caseReducers.showToast(state, {
          payload: { success: false, message: action.payload?.message },
        });
      })

      // --- Quest Type confirm purchase ----------------------------------
      .addCase(questTypeConfirmPurchase.pending, (state, action) => {
        state.confirmStatus = "loading";
        action.meta.arg?.setLoader?.(true);
      })
      .addCase(questTypeConfirmPurchase.fulfilled, (state, action) => {
        state.confirmStatus = "succeeded";
        state.cart = [];
        state.marketplaceStatus = "idle";
        action.meta.arg?.setLoader?.(false);
        questTypePackageSlice.caseReducers.showToast(state, {
          payload: {
            success: true,
            message:
              action.payload?.message || "Quest types purchased successfully",
          },
        });
      })
      .addCase(questTypeConfirmPurchase.rejected, (state, action) => {
        state.confirmStatus = "failed";
        action.meta.arg?.setLoader?.(false);
        // 422 = the payment was already consumed, so access is already granted.
        // Drop the cart and let the marketplace refresh show the real state.
        if (action.payload?.status === 422) {
          state.cart = [];
        }
        state.marketplaceStatus = "idle";
        questTypePackageSlice.caseReducers.showToast(state, {
          payload: { success: false, message: action.payload?.message },
        });
      })

      .addMatcher(
        (action) =>
          [
            questTypePackageList.pending,
            createPaymentIntent.pending,
            confirmPurchase.pending,
            claimFreePackage.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          state.error = null;
          action.meta.arg.setLoader(true);
        }
      )
      .addMatcher(
        (action) =>
          [
            questTypePackageList.fulfilled,
            createPaymentIntent.fulfilled,
            confirmPurchase.fulfilled,
            claimFreePackage.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";

          if (["listQuestTypePackage"].includes(action.type.split("/")[0])) {
            const existingData = state.questTypePackageData.data;
            const newData = action.payload?.categories || [];

            const mergedData = [...existingData, ...newData].reduce(
              (acc, current) => {
                if (!acc.find((item) => item.id === current.id)) {
                  acc.push(current);
                }
                return acc;
              },
              []
            );

            state.questTypePackageData = {
              ...action.payload,
              data: mergedData,
            };
          }

          // Confirm Purchase / Claim Free - reset and refresh list
          if (
            ["confirmPurchase", "claimFreePackage"].includes(
              action.type.split("/")[0]
            )
          ) {
            state.questTypePackageData = {
              data: [],
            };
            state.status = "idle";
          }

          action.meta.arg.setLoader(false);

          // Show toast only in case of confirm purchase or claim free
          if (
            ["confirmPurchase", "claimFreePackage"].includes(
              action.type.split("/")[0]
            )
          ) {
            questTypePackageSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message ||
                  (action.type.split("/")[0] === "claimFreePackage"
                    ? "Quest type package claimed successfully"
                    : "Quest type package purchased successfully"),
              },
            });
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            questTypePackageList.rejected,
            createPaymentIntent.rejected,
            confirmPurchase.rejected,
            claimFreePackage.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          state.error = action.payload;
          action.meta.arg.setLoader(false);
          questTypePackageSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

export const {
  resetQuestTypePackageState,
  addToCart,
  removeFromCart,
  clearCart,
} = questTypePackageSlice.actions;
export default questTypePackageSlice.reducer;
